import React, { useState } from "react";
import { useTheme } from "./ThemeContext";
import { Helmet } from "react-helmet";


export default function ColorTools() {
  const { darkMode } = useTheme();
  const [color, setColor] = useState("#3498db");
  const [palette, setPalette] = useState(generatePalette("#3498db"));

  const handleChange = (e) => {
    setColor(e.target.value);
    setPalette(generatePalette(e.target.value));
  };

  const schemaOrgJSONLD = {
    "@context": "http://schema.org",
    "@type": "WebPage",
    "name": "Color Tools - Generate Color Palettes and Convert Color Formats",
    "description": "An interactive color tool for generating color palettes and converting between color formats (HEX, RGB, HSL, CMYK).",
    "url": window.location.href, // You can dynamically get this URL
    "mainEntityOfPage": "True",
  };

  return (

    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(schemaOrgJSONLD)}
        </script>
        <title>Color Tools - Explore and Copy Color Palettes</title>
        <meta name="description" content="Use our interactive color tool to generate color palettes, convert between color formats, and find contrast suggestions for web accessibility." />
        <meta name="keywords" content="color palette, hex to rgb, hex to cmyk, color contrast, color generator, color tools, web accessibility" />
        <meta property="og:title" content="Color Tools - Explore and Copy Color Palettes" />
        <meta property="og:description" content="Explore color palettes, convert between color formats, and check contrast with our easy-to-use color tool." />
        <meta property="og:image" content="URL_TO_IMAGE" />
      </Helmet>
      <div className={`min-h-screen p-6 ${darkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-900"}`}>
        <div className={`max-w-screen-2xl mx-auto p-6 rounded-lg shadow-md ${darkMode ? "bg-gray-800" : "bg-white"}`}>
          <h2 className="text-xl font-semibold mb-4">Color Tools</h2>

          {/* Color Picker */}
          <input
            type="color"
            value={color}
            onChange={handleChange}
            className="w-full h-12 cursor-pointer border-none outline-none"
          />

          {/* Color Info */}
          <div className="mt-4 p-4 rounded-lg" style={{ backgroundColor: color }}>
            <p>Hex: <CopyableText text={color} /></p>
            <p>RGB: <CopyableText text={hexToRgb(color)} /></p>
            <p>HSL: <CopyableText text={hexToHsl(color)} /></p>
            <p>CMYK: <CopyableText text={hexToCmyk(color)} /></p>
            <p>Contrast: {getContrast(color)}</p>
          </div>

          {/* Color Palette Generator */}
          <h3 className="mt-6 text-lg font-medium">Generated Palette</h3>
          <div className="grid grid-cols-5 gap-2 mt-2">
            {palette.map((col, index) => (
              <div
                key={index}
                className="h-12 rounded-lg flex items-center justify-center cursor-pointer"
                style={{ backgroundColor: col }}
                onClick={() => setColor(col)}
              >
                <CopyableText text={col} />
              </div>
            ))}
          </div>
          <div className="mt-8">
            <h3 className="text-lg font-medium mb-4">How to Use the Color Tools</h3>
            <p className="mb-4">
              This interactive tool allows you to generate color palettes and convert color formats. Here's how you can use it:
            </p>

            <h4 className="font-semibold">1. Choose a Color</h4>
            <p>Select a color from the color picker. You can either pick a color visually or input a hex value to start.</p>

            <h4 className="font-semibold">2. View Color Information</h4>
            <p>After selecting a color, you will see detailed information about it:</p>
            <ul className="list-disc pl-6 mb-4">
              <li><strong>Hex:</strong> The hexadecimal representation of the color (e.g., #3498db).</li>
              <li><strong>RGB:</strong> The RGB format (Red, Green, Blue), which is commonly used in web design.</li>
              <li><strong>HSL:</strong> The HSL format (Hue, Saturation, Lightness), useful for color manipulation.</li>
              <li><strong>CMYK:</strong> The CMYK format (Cyan, Magenta, Yellow, Key/Black), often used in printing.</li>
              <li><strong>Contrast:</strong> This suggests whether black or white text is more readable on the selected color.</li>
            </ul>

            <h4 className="font-semibold">3. Generate a Palette</h4>
            <p>The tool will generate a color palette based on your selected color. The palette includes variations of your color, which can be used for designing cohesive color schemes.</p>

            <h4 className="font-semibold">4. Copy Colors</h4>
            <p>You can copy any of the color values by clicking the color text. The color code will be copied to your clipboard for easy use.</p>

            <h4 className="font-semibold">5. Use the Guide</h4>
            <p>We've provided a detailed guide above to help you understand color formats and how to use them in different contexts (web design, graphic design, and printing).</p>
          </div>
        </div>
        
      </div>
    </>


  );
}

// Copyable text component
const CopyableText = ({ text }) => {
  const copyToClipboard = () => {
    navigator.clipboard.writeText(text);
    alert(`Copied: ${text}`);
  };
  return (
    <span className="cursor-pointer underline" onClick={copyToClipboard}>
      {text}
    </span>
  );
};

// Convert Hex to RGB
function hexToRgb(hex) {
  let r = parseInt(hex.slice(1, 3), 16);
  let g = parseInt(hex.slice(3, 5), 16);
  let b = parseInt(hex.slice(5, 7), 16);
  return `rgb(${r}, ${g}, ${b})`;
}

// Convert Hex to HSL
function hexToHsl(hex) {
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;

  let max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    let d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;

    }
    h /= 6;
  }
  return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
}

// Convert Hex to CMYK
function hexToCmyk(hex) {
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;

  let k = 1 - Math.max(r, g, b);
  let c = (1 - r - k) / (1 - k) || 0;
  let m = (1 - g - k) / (1 - k) || 0;
  let y = (1 - b - k) / (1 - k) || 0;

  return `cmyk(${Math.round(c * 100)}%, ${Math.round(m * 100)}%, ${Math.round(y * 100)}%, ${Math.round(k * 100)}%)`;
}

// Generate a Color Palette
function generatePalette(baseColor) {
  let hsl = hexToHsl(baseColor).match(/\d+/g);
  if (!hsl) return [];

  let h = parseInt(hsl[0]);
  let s = parseInt(hsl[1]);
  let l = parseInt(hsl[2]);

  let shades = [-40, -20, 0, 20, 40]; // Vary lightness for palette
  return shades.map(offset => `hsl(${h}, ${s}%, ${Math.max(10, Math.min(90, l + offset))}%)`);
}

// Get Contrast (White or Black)
function getContrast(hex) {
  let r = parseInt(hex.slice(1, 3), 16);
  let g = parseInt(hex.slice(3, 5), 16);
  let b = parseInt(hex.slice(5, 7), 16);

  let brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? "Black Text Recommended" : "White Text Recommended";
}
